<script lang="ts">
  // Our OWN booster-pack / structure-deck wrapper. Instead of ygoprodeck's box art
  // we compose a custom foil wrapper: the hero card's cropped artwork up top, a holo
  // sheen, a kind ribbon, and a neon title plate, branded to the game.
  import { cardArt, cardImage } from "../../data.ts";

  let {
    name,
    art,
    kind = "pack",
    class: cls = "",
  }: {
    name: string;
    /** Passcode of the hero card whose cropped artwork fills the wrapper. */
    art?: number;
    kind?: "pack" | "deck";
    class?: string;
  } = $props();

  // Cropped art (ygoprodeck) → fall back to our self-hosted full card if it 404s/429s.
  let broke = $state(false);
  const src = $derived(art == null ? "" : broke ? cardImage(art) : cardArt(art));
  const label = $derived(kind === "deck" ? "Structure Deck" : "Booster Pack");
</script>

<div class="wrap {kind} {cls}" style="--c:8px">
  <div class="bg grid-tex"></div>

  {#if art != null}
    <img class="hero" src={src} alt="" onerror={() => (broke = true)} draggable="false" />
  {/if}
  <div class="scrim"></div>
  <div class="sheen"></div>

  <!-- top foil ribbon: kind + brandmark -->
  <div class="ribbon">
    <span class="kind">{label}</span>
    <span class="brand">◆</span>
  </div>

  <!-- bottom title plate, riding the foil scrim -->
  <div class="plate">
    <span class="title">{name}</span>
  </div>
</div>

<style>
  .wrap {
    position: relative;
    width: 100%;
    aspect-ratio: 3 / 4;
    overflow: hidden;
    clip-path: polygon(var(--c) 0, 100% 0, 100% calc(100% - var(--c)), calc(100% - var(--c)) 100%, 0 100%, 0 var(--c));
    background: linear-gradient(165deg, #0a1430, #05070f 70%);
    box-shadow: inset 0 0 0 1.5px var(--edge), inset 0 0 18px color-mix(in oklab, var(--edge) 22%, transparent);
    --edge: var(--color-cyan);
    --tint: var(--color-cyan);
  }
  .wrap.deck { --edge: var(--color-gold); --tint: var(--color-gold); }

  .bg { position: absolute; inset: 0; opacity: 0.5; }

  /* Hero artwork fills the WHOLE wrapper, a full-bleed foil pack, no dead band. */
  .hero {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center 28%;
  }
  /* Legibility scrim, darkens only the lower portion where the name sits, so the
     art reads edge-to-edge with no empty black panel below it. */
  .scrim {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: linear-gradient(
      to top,
      var(--color-void) 3%,
      color-mix(in oklab, var(--color-void) 80%, transparent) 26%,
      color-mix(in oklab, var(--color-void) 22%, transparent) 46%,
      transparent 62%
    );
  }
  /* Diagonal holographic sheen. */
  .sheen {
    position: absolute;
    inset: 0;
    pointer-events: none;
    mix-blend-mode: overlay;
    background: linear-gradient(115deg, transparent 30%, color-mix(in oklab, var(--tint) 45%, white 30%) 48%, transparent 66%);
    background-size: 250% 250%;
    animation: pack-sheen 4.2s linear infinite;
    opacity: 0.5;
  }
  @keyframes pack-sheen {
    0% { background-position: 130% 0; }
    100% { background-position: -50% 0; }
  }

  .ribbon {
    position: absolute;
    inset: 0 0 auto 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 5px 8px;
    background: linear-gradient(90deg, color-mix(in oklab, var(--edge) 85%, transparent), transparent);
  }
  .kind {
    font-family: Rajdhani, system-ui, sans-serif;
    font-size: 0.52rem;
    font-weight: 800;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--color-void);
  }
  .brand {
    font-size: 0.6rem;
    color: var(--color-void);
    opacity: 0.85;
  }

  .plate {
    position: absolute;
    inset: auto 0 0 0;
    padding: 8px 9px 11px;
    text-align: center;
  }
  /* A thin foil hairline above the name, the pack's bottom seam. */
  .plate::before {
    content: "";
    position: absolute;
    left: 12%;
    right: 12%;
    top: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, color-mix(in oklab, var(--tint) 70%, transparent), transparent);
  }
  .title {
    display: block;
    font-family: Rajdhani, system-ui, sans-serif;
    font-size: 0.82rem;
    font-weight: 700;
    line-height: 1.08;
    letter-spacing: 0.01em;
    color: var(--color-ink, #eaf6ff);
    text-shadow: 0 1px 4px rgba(0, 0, 0, 0.95), 0 0 12px color-mix(in oklab, var(--tint) 45%, transparent);
  }

  @media (prefers-reduced-motion: reduce) {
    .sheen { animation: none; }
  }
</style>
